import { useTranslation } from "react-i18next";

import type { RatingEntry } from "@/entities/rating";
import { formatMoney, staggerIndex } from "@/shared/lib";
import { Avatar, Panel } from "@/shared/ui";

/**
 * Таблица участников начиная с четвёртого места — первые три уже на подиуме.
 *
 * Своя строка подсвечена по полю `is_me` с бэкенда, а не сравнением с id
 * профиля: иначе экран рейтинга был бы вынужден грузить ещё и профиль ради
 * одной рамки.
 *
 * Высота строки задана `min-height`, а не фиксированной: в макете это 32.5px
 * при кегле 9px, но кегли пересчитаны под читаемые, и длинное имя в жёсткую
 * строку уже не влезает.
 */
export function RatingTable({ rows }: { rows: RatingEntry[] }) {
  const { t } = useTranslation();

  if (rows.length === 0) return null;

  return (
    <Panel as="section" radius="md" className="overflow-hidden">
      <div className="grid grid-cols-[28px_1fr_auto_auto] items-center gap-2 px-3 py-2.5 text-caption text-text-secondary">
        <span />
        <span>{t("rating.table.user")}</span>
        <span className="text-right">{t("rating.table.robots")}</span>
        <span className="w-[104px] text-right">{t("rating.table.earned")}</span>
      </div>

      <ul>
        {rows.map((entry, index) => (
          <li
            key={entry.rank}
            className={[
              "stagger grid min-h-11 grid-cols-[28px_1fr_auto_auto] items-center gap-2 px-3 py-2",
              "border-t border-border-hair",
              entry.is_me ? "bg-surface-selected" : "",
            ].join(" ")}
            style={staggerIndex(index)}
          >
            <span className="text-body font-medium text-text-secondary">{entry.rank}</span>

            <span className="flex min-w-0 items-center gap-2">
              <Avatar src={entry.avatar_url} initial={entry.initial} size={25} />
              <span className="truncate text-body font-medium text-text-primary">
                {entry.display_name}
              </span>
            </span>

            <span className="text-caption whitespace-nowrap text-text-secondary">
              {t("rating.robotsCount", { count: entry.robots_count })}
            </span>

            <span className="w-[104px] text-right text-small font-semibold text-status-profit">
              {formatMoney(entry.earned, { sign: "always", suffix: "USDT" })}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
